import { useCallback, useEffect, useMemo, useState, useId } from 'react'
import { listScheduleRules, createScheduleRule, type ScheduleRuleSummary } from '../../services/api/formSchedules'
import { useToast } from '../ToastContainer'
import { FormType } from '@/types'

interface TemplateSchedulePanelProps {
  templateId: string
  templateVersionId?: string
  projectId?: string | null
  formType?: FormType
}

const periodicFrequencyOptions: Array<{ label: string; value: string }> = [
  { label: '每日', value: 'DAILY' },
  { label: '每週', value: 'WEEKLY' },
  { label: '每月', value: 'MONTHLY' },
  { label: '每季', value: 'QUARTERLY' },
  { label: '每年', value: 'YEARLY' },
]

const oneOffFrequencyOptions: Array<{ label: string; value: string }> = [
  { label: '單次', value: 'ONCE' },
]

const frequencyLabelMap: Record<string, string> = {
  ONCE: '單次',
  DAILY: '每日',
  WEEKLY: '每週',
  MONTHLY: '每月',
  QUARTERLY: '每季',
  YEARLY: '每年',
  CUSTOM: '自訂',
}

const TemplateSchedulePanel = ({ templateId, templateVersionId, projectId, formType }: TemplateSchedulePanelProps) => {
  const { showError, showSuccess } = useToast()
  const baseId = useId()
  const [loading, setLoading] = useState(false)
  const [creating, setCreating] = useState(false)
  const [rules, setRules] = useState<ScheduleRuleSummary[]>([])
  const [formState, setFormState] = useState({
    name: '',
    frequency: formType === FormType.ONE_OFF ? 'ONCE' : 'WEEKLY',
    interval: 1,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Taipei',
    startDate: new Date().toISOString().slice(0, 10),
    dueInDays: 1,
  })

  const availableFrequencies = useMemo(() => {
    if (formType === FormType.ONE_OFF) {
      return oneOffFrequencyOptions
    }
    return periodicFrequencyOptions
  }, [formType])

  const fieldIds = useMemo(
    () => ({
      name: `${baseId}-schedule-name`,
      frequency: `${baseId}-schedule-frequency`,
      interval: `${baseId}-schedule-interval`,
      startDate: `${baseId}-schedule-start-date`,
      dueInDays: `${baseId}-schedule-due-in-days`,
      timezone: `${baseId}-schedule-timezone`,
    }),
    [baseId],
  )

  useEffect(() => {
    setFormState(prev => {
      if (availableFrequencies.some(option => option.value === prev.frequency)) {
        return prev
      }
      const fallback = availableFrequencies[0]?.value ?? prev.frequency
      return {
        ...prev,
        frequency: fallback,
      }
    })
  }, [availableFrequencies])

  useEffect(() => {
    if (formState.frequency === 'ONCE' && formState.interval !== 1) {
      setFormState(prev => ({
        ...prev,
        interval: 1,
      }))
    }
  }, [formState.frequency, formState.interval])

  const loadRules = useCallback(async () => {
    if (!templateId) return
    try {
      setLoading(true)
      const response = await listScheduleRules({ templateId })
      setRules(response.data?.rules ?? [])
    } catch (error: any) {
      showError('讀取排程失敗', error?.message || '請稍後再試')
    } finally {
      setLoading(false)
    }
  }, [templateId, showError])

  useEffect(() => {
    loadRules()
  }, [loadRules])

  const handleCreateRule = async () => {
    if (!formState.name.trim()) {
      showError('建立排程失敗', '請輸入排程名稱')
      return
    }
    try {
      setCreating(true)
      const intervalValue = formState.frequency === 'ONCE' ? undefined : Number(formState.interval) || 1
      await createScheduleRule({
        name: formState.name.trim(),
        templateId,
        templateVersionId,
        projectId: projectId ?? undefined,
        frequency: formState.frequency,
        interval: intervalValue,
        timezone: formState.timezone,
        startDate: formState.startDate,
        config: { dueInDays: Number(formState.dueInDays) || 1 },
      })
      showSuccess('排程已建立')
      setFormState(prev => ({ ...prev, name: '' }))
      loadRules()
    } catch (error: any) {
      showError('建立排程失敗', error?.message || '請稍後再試')
    } finally {
      setCreating(false)
    }
  }

  const scheduleList = useMemo(
    () =>
      rules.sort((a, b) => {
        if (a.nextRunAt && b.nextRunAt) {
          return a.nextRunAt.localeCompare(b.nextRunAt)
        }
        if (a.nextRunAt) return -1
        if (b.nextRunAt) return 1
        return a.name.localeCompare(b.name)
      }),
    [rules],
  )

  const isOneOff = formType === FormType.ONE_OFF

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-900">排程設定</h2>
        <span className="text-xs text-gray-500">
          {isOneOff ? '設定單次派送時間' : '自動派送巡檢任務'}
        </span>
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700" htmlFor={fieldIds.name}>
              排程名稱
            </label>
            <input
              type="text"
              value={formState.name}
              onChange={(e) => setFormState(prev => ({ ...prev, name: e.target.value }))}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
              placeholder="例：每週巡檢"
              id={fieldIds.name}
            />
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700" htmlFor={fieldIds.frequency}>
                頻率
              </label>
              <select
                value={formState.frequency}
                onChange={(e) => setFormState(prev => ({ ...prev, frequency: e.target.value }))}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                id={fieldIds.frequency}
              >
                {availableFrequencies.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="w-24">
              <label className="block text-sm font-medium text-gray-700" htmlFor={fieldIds.interval}>
                間隔
              </label>
              <input
                type="number"
                min={1}
                value={formState.interval}
                onChange={(e) => setFormState(prev => ({ ...prev, interval: Number(e.target.value) }))}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-100"
                disabled={formState.frequency === 'ONCE'}
                id={fieldIds.interval}
              />
            </div>
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700" htmlFor={fieldIds.startDate}>
                開始日期
              </label>
              <input
                type="date"
                value={formState.startDate}
                onChange={(e) => setFormState(prev => ({ ...prev, startDate: e.target.value }))}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                id={fieldIds.startDate}
              />
            </div>
            <div className="w-28">
              <label className="block text-sm font-medium text-gray-700" htmlFor={fieldIds.dueInDays}>
                完成期限(天)
              </label>
              <input
                type="number"
                min={0}
                value={formState.dueInDays}
                onChange={(e) => setFormState(prev => ({ ...prev, dueInDays: Number(e.target.value) }))}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                id={fieldIds.dueInDays}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700" htmlFor={fieldIds.timezone}>
              時區
            </label>
            <input
              type="text"
              value={formState.timezone}
              onChange={(e) => setFormState(prev => ({ ...prev, timezone: e.target.value }))}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
              id={fieldIds.timezone}
            />
          </div>
          <button
            type="button"
            onClick={handleCreateRule}
            disabled={creating}
            className="inline-flex items-center justify-center rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {creating ? '建立中…' : '新增排程'}
          </button>
        </div>

        <div className="max-h-72 overflow-y-auto rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
          {loading && <p className="text-sm text-gray-500">讀取排程中…</p>}
          {scheduleList.length === 0 ? (
            !loading && <p className="text-sm text-gray-500">尚未建立排程。</p>
          ) : (
            <ul className="space-y-2">
              {scheduleList.map(rule => (
                <li key={rule.id} className="rounded border border-gray-200 bg-white px-3 py-2">
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-gray-900">{rule.name}</div>
                    <span className="text-xs text-gray-500">{rule.status}</span>
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    <div>頻率：{frequencyLabelMap[rule.frequency] ?? rule.frequency}</div>
                    {rule.frequency !== 'ONCE' && (
                      <div>間隔：{rule.interval}</div>
                    )}
                    <div>下次執行：{rule.nextRunAt ? new Date(rule.nextRunAt).toLocaleString() : '—'}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}

export default TemplateSchedulePanel
